"use client"

import { authClient, useSession } from "@zentjes/auth/client"
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"

export type SessionType = "zero_session" | "authenticated_user"

type ZeroID = {
  id: string
  crudCount?: number
}

type UserPreferences = Record<string, unknown>

type User = {
  id: string
  email: string
  name: string
  avatar?: string
  username?: string
  role?: string
  location?: string
  pronouns?: string
  website?: string
  bio?: string
  currency?: string
  preferences?: UserPreferences
}

type Session = {
  id: string
  type: SessionType
  userId: string
  zeroId: string
  expiresAt: string
}

interface AuthContextValue {
  user: User | null
  session: Session | null
  zeroID: ZeroID | null
  sessionType: SessionType
  isPending: boolean
  showAuthModal: boolean
  setShowAuthModal: (show: boolean) => void
  requireAuth: boolean
  setRequireAuth: (require: boolean) => void
  updateUser: (data: Partial<User>) => void
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, name: string) => Promise<void>
  signOut: () => Promise<void>
  executeCrudCallback: <T>(callback: () => T | Promise<T>) => Promise<T | null>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    return {} as AuthContextValue
  }
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: betterAuthSession, isPending: betterAuthPending } = useSession()
  // const betterAuthSession: any = null;
  // const betterAuthPending = false;

  const [sessionType, setSessionTypeState] = useState<SessionType>("zero_session")
  const [zeroID, setZeroID] = useState<ZeroID | null>(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [requireAuth, setRequireAuth] = useState(false)
  const [localUser, setLocalUser] = useState<User | null>(null)

  useEffect(() => {
    const savedZeroID = localStorage.getItem("zeroID")
    if (savedZeroID) {
      setZeroID({ id: savedZeroID })
    } else {
      const newZeroID = crypto.randomUUID()
      localStorage.setItem("zeroID", newZeroID)
      setZeroID({ id: newZeroID })
    }
  }, [])

  useEffect(() => {
    if (betterAuthSession?.user && sessionType === "zero_session") {
      setSessionTypeState("authenticated_user")
    }
  }, [betterAuthSession, sessionType])

  const user: User | null = localUser ?? (betterAuthSession?.user
    ? {
      id: betterAuthSession.user.id,
      email: betterAuthSession.user.email,
      name: betterAuthSession.user.name,
      avatar: betterAuthSession.user.image || undefined,
    }
    : null)

  const session: Session | null =
    sessionType !== "zero_session" && user
      ? {
        id: `session_${user.id}`,
        type: sessionType,
        userId: user.id,
        zeroId: zeroID?.id || "",
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      }
      : null

  const updateUser = useCallback((data: Partial<User>) => {
    setLocalUser((prev) => (prev ? { ...prev, ...data } : null))
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      await authClient.signIn.email(
        { email, password },
        {
          onSuccess: () => {
            setSessionTypeState("authenticated_user")
          },
        }
      )
    } catch (error) {
      console.error("Sign in failed:", error)
      throw error
    }
  }, [])

  const signUp = useCallback(async (email: string, password: string, name: string) => {
    try {
      await authClient.signUp.email(
        { email, password, name },
        {
          onSuccess: () => {
            setSessionTypeState("authenticated_user")
          },
        }
      )
    } catch (error) {
      console.error("Sign up failed:", error)
      throw error
    }
  }, [])

  const signOut = useCallback(async () => {
    await authClient.signOut()
    setSessionTypeState("zero_session")
    setLocalUser(null)
    window.location.href = "/"
  }, [])

  const executeCrudCallback = useCallback(
    async <T,>(callback: () => T | Promise<T>): Promise<T | null> => {
      return await callback()
    },
    [sessionType],
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        zeroID,
        sessionType,
        isPending: betterAuthPending,
        showAuthModal,
        setShowAuthModal,
        requireAuth,
        setRequireAuth,
        updateUser,
        signIn,
        signUp,
        signOut,
        executeCrudCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
